import React, { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useAppDispatch } from '../../app/hooks';
import Offset from './Offset';
import Upload from '../molecules/Upload';
import EditForm from './EditForm';
import {
  createVenueContact,
  updateVenueContact,
  deleteVenueContact,
} from '../../features/venue/venueSlice';
import {
  createServiceProviderContact,
  updateServiceProviderContact,
  deleteServiceProviderContact,
} from '../../features/serviceProvider/serviceProviderSlice';
import {
  createContact,
  updateContact,
  deleteContact,
} from '../../lib/apis/contact';
import {
  upload_image,
  delete_image,
  convertImageIDToURL,
} from '../../lib/apis/image';
import { departments } from '../../data/display';
import { clearObj, getErrorMsg } from '../../lib/utils';

import Input from '../atoms/Input';
import { stubFalse } from 'lodash';
import { Styles } from '@hubspot/api-client/lib/codegen/cms/blogs/blog_posts';

interface VenueContactsFormProps {
  venueID?: string;
  serviceProviderID?: string;
  prefill?: any;
  onSubmit?: (id: string, data: any) => void;
  onCancel?: () => void;
}

const VenueContactsForm = (props: VenueContactsFormProps) => {
  const { t } = useTranslation(['common']);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [selectedType, setSelectedType] = useState();
  const [files, setFiles] = useState([]) as any;
  const [removedFiles, setRemovedFiles] = useState([]) as any;
  const { venueID, serviceProviderID, prefill, onSubmit, onCancel } = props;

  const updateItemState = !!venueID
    ? updateVenueContact
    : updateServiceProviderContact;
  const deleteState = !!venueID
    ? deleteVenueContact
    : deleteServiceProviderContact;
  const createState = !!venueID
    ? createVenueContact
    : createServiceProviderContact;

  const formID = 'contactForm';

  const {
    id,
    title,
    name,
    email,
    phone,
    image,
    sales_contact_venue_id,
    management_contact_venue_id,
    sales_contact_service_provider_id,
    management_contact_service_provider_id,
  } = (prefill as any) || {};

  const type =
    selectedType ||
    (!!sales_contact_venue_id || !!sales_contact_service_provider_id
      ? 'sales'
      : !!management_contact_venue_id ||
        !!management_contact_service_provider_id
      ? 'management'
      : 'sales');

  const departmentOptions = Object.keys(departments).map((el) => ({
    value: el,
    label: t(`common:${el}`),
  }));
  /**
   * Handle errors
   */
  const handleError = (error: any) => {
    const errorMsg = getErrorMsg(error);
    setError(errorMsg);
    setLoading(false);
  };

  /**
   * Handle contact input
   */

  // Add ContactCard
  const createBusinessContact = (obj: any) => {
    try {
      // Save new ContactCard to database and update venueState
      createContact(obj)
        .then((response) => {
          console.log('createContact', obj, response);
          dispatch(createState(response));
          updateFiles(response);
        })
        .catch((error) => {
          handleError(error);
        });
    } catch (error) {
      handleError(error);
    }
  };

  // Update ContactCard
  const updateBusinessContact = (id: string, obj: any) => {
    try {
      // Save updated ContactCard to database and update venueState
      updateContact(id, obj)
        .then((response) => {
          console.log('updateContact', obj, response);
          updateFiles(response);
        })
        .catch((error) => {
          handleError(error);
          return false;
        });
    } catch (error) {
      handleError(error);
    }
  };

  // Delete ContactCard
  const deleteBusinessContact = (id: string, obj: any) => {
    try {
      // Delete ContactCard to database and update venueState
      deleteContact(id)
        .then((response) => {
          console.log('deleteContact', obj, response);
          setLoading(false);
          dispatch(deleteState(response));
          if (!!onSubmit) {
            onSubmit(formID, response);
          }
        })
        .catch((error) => {
          handleError(error);
          return false;
        });
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Handle images
   */

  const toggleImageError = (error?: any) => {
    const add = error ? ['contactImage'] : [];
    const newErrors = [
      ...fieldErrors.filter((el: string) => el !== 'contactImage'),
      ...add,
    ];
    console.log('toggleImageError', newErrors);
    setFieldErrors(newErrors);
  };

  const onFileRemove = (file: any) => {
    toggleImageError();
    const { image_id } = file;
    if (!image_id) {
      return;
    }
    setRemovedFiles([
      ...removedFiles.filter((str: string) => str !== image_id),
      image_id,
    ]);
  };

  const updateFiles = async (payload: any) => {
    for (const image_id of removedFiles) {
      await delete_image(image_id)
        .then((response: any) => {
          console.log('deletedFile', image_id);
        })
        .catch((error: any) => {
          handleError(error);
        });
    }
    const currFile =
      !!image && removedFiles.indexOf(image.image_id) > -1 ? undefined : image;

    const newFiles = files;
    if (!!newFiles.length) {
      const uploaded = [] as any;
      console.log('upload images', newFiles);
      for (const file of newFiles) {
        upload_image('contact', 'single', payload.id, file as any)
          .then((response: any) => {
            // logoField.value = '';
            uploaded.push(response);
            console.log('uploaded image', response);
            if (uploaded.length === newFiles.length) {
              // field.value = '';
              completeSubmit({
                ...payload,
                image: uploaded[0],
              });
            }
          })
          .catch((error) => {
            handleError(error);
            return false;
          });
      }
    } else {
      completeSubmit({ ...payload, image: currFile });
    }
  };

  const completeSubmit = (result: any) => {
    console.log('completeUpdate', result);
    setRemovedFiles([]);
    setLoading(false);
    dispatch(updateItemState(result));
    if (!!onSubmit) {
      onSubmit(formID, result);
    }
  };

  /**
   * Handle form submit
   */
  const submit = (formData: any, deleteMode?: boolean) => {
    const { id, type } = formData;
    if (fieldErrors.length) {
      setError(t('common:form-input-invalid'));
      return;
    }
    const payload = {
      ...clearObj({
        ...formData,
        id: null,
        type: null,
        contactImage: null,
      }),
      sales_contact_venue_id: !!venueID && type === 'sales' ? venueID : null,
      management_contact_venue_id:
        !!venueID && type === 'management' ? venueID : null,
      sales_contact_service_provider_id:
        !!serviceProviderID && type === 'sales' ? serviceProviderID : null,
      management_contact_service_provider_id:
        !!serviceProviderID && type === 'management' ? serviceProviderID : null,
    };
    setLoading(true);
    if (!!deleteMode && !!id) {
      deleteBusinessContact(id, payload);
    } else if (!!id) {
      updateBusinessContact(id, payload);
    } else {
      createBusinessContact(payload);
    }
  };

  const cancel = () => {
    if (!!onCancel) {
      onCancel();
    }
  };

  return (
    <EditForm
      checkOnce
      noCols
      deletable={!!id}
      blocked={!!fieldErrors.length}
      id={formID}
      title={t('common:contact_details')}
      error={error}
      loading={loading}
      onSubmit={submit}
      onCancel={cancel}
      onFieldError={(arr: any) => {
        if (fieldErrors.indexOf('contactImage') > -1) {
          setFieldErrors([...arr, 'contactImage']);
        } else {
          setFieldErrors(arr);
        }
      }}
    >
      <input name="id" type="hidden" defaultValue={id || undefined} />
      <input name="type" type="hidden" defaultValue={type} />
      <Offset>
        <Input
          half
          reset
          name="name"
          icon="name"
          error={fieldErrors.indexOf('name') > -1}
          label={t('common:full_name')}
          defaultValue={name || undefined}
        />
        <Input
          half
          name="title"
          icon="name"
          label={t('common:department')}
          defaultValue={title || departmentOptions[0].value}
          options={departmentOptions}
          onChange={(obj) => {
            const currType = departments[obj.title] || 'sales';
            // setSelectedTitle(obj.title);
            setSelectedType(currType);
          }}
        />
        <Input
          half
          reset
          name="email"
          icon="email"
          error={fieldErrors.indexOf('email') > -1}
          label={t('common:email')}
          defaultValue={email || undefined}
        />
        <Input
          half
          optional
          reset
          format="phone"
          name="phone"
          icon="phone"
          error={fieldErrors.indexOf('phone') > -1}
          label={t('common:phone')}
          defaultValue={phone || undefined}
          placeholder={t(`common:input-phone-exp`, {
            countryCode: '49',
          })}
          unit={t(`common:input-phone-help`)}
        />
        <Upload
          full
          optional
          type="image"
          name={'contactImage'}
          data={image}
          onChange={(files) => {
            setFiles(files);
          }}
          onError={toggleImageError}
          onRemove={onFileRemove}
        />
      </Offset>
    </EditForm>
  );
};

export default VenueContactsForm;
